/**
 * User Management - TB Mukt Bharat Admin
 * Fast, lightweight component using GovLayout
 *
 * Uses the guarded /admin/users endpoint with SERVER-SIDE search + combined
 * filters (role, user type, status) so filtering operates on the full user
 * base, not just the current page of 10.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Search, Filter, ChevronLeft, ChevronRight, UserCheck, Mail, Shield, Calendar, X, Download, FileText } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

// ================================================================================
// TYPES
// ================================================================================
interface User {
  id: number
  uuid: string
  email: string
  firstName: string
  lastName: string
  username: string
  role: string
  userType?: string
  status: string
  emailVerified: boolean
  phoneVerified: boolean
  createdAt: string
  lastLogin: string | null
}

// ================================================================================
// USER ROW COMPONENT - Memoized for performance
// ================================================================================
const UserRow = React.memo(({ user }: { user: User }) => {
  const roleStyles: Record<string, string> = {
    SUPER_ADMIN: 'bg-red-100 text-red-700',
    ADMIN: 'bg-blue-100 text-blue-700',
    USER: 'bg-gray-100 text-gray-700',
  }

  const statusStyles: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-700',
    INACTIVE: 'bg-yellow-100 text-yellow-700',
    SUSPENDED: 'bg-red-100 text-red-700',
    BANNED: 'bg-red-100 text-red-700',
  }

  const userTypeStyles: Record<string, string> = {
    USER: 'bg-gray-100 text-gray-600',
    NGO: 'bg-indigo-100 text-indigo-700',
    NIKSHAY_MITRA: 'bg-cyan-100 text-cyan-700',
    SUPPORTER_INDIVIDUAL: 'bg-sky-100 text-sky-700',
    SUPPORTER_NGO: 'bg-blue-100 text-blue-700',
    SUPPORTER_CORPORATE: 'bg-violet-100 text-violet-700',
    NUTRITION_SPONSOR: 'bg-amber-100 text-amber-700',
    VOLUNTEER_COMMUNITY: 'bg-rose-100 text-rose-700',
  }

  const userTypeLabel: Record<string, string> = {
    USER: 'User',
    NGO: 'NGO',
    NIKSHAY_MITRA: 'Ni-kshay Mitra',
    SUPPORTER_INDIVIDUAL: 'Supporter (Individual)',
    SUPPORTER_NGO: 'Supporter (NGO)',
    SUPPORTER_CORPORATE: 'Supporter (Corporate)',
    NUTRITION_SPONSOR: 'Nutrition Sponsor',
    VOLUNTEER_COMMUNITY: 'Volunteer (Community)',
  }

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
            {user.firstName?.[0]}{user.lastName?.[0]}
          </div>
          <div>
            <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
            <p className="text-sm text-gray-500">@{user.username}</p>
          </div>
        </div>
      </td>
      <td className="p-4">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-gray-700">{user.email}</p>
            {user.emailVerified && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600">
                <UserCheck className="w-3 h-3" /> Verified
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="p-4">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${roleStyles[user.role] || roleStyles.USER}`}>
          <Shield className="w-3 h-3" />
          {user.role}
        </span>
      </td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${userTypeStyles[user.userType || 'USER'] || userTypeStyles.USER}`}>
          {userTypeLabel[user.userType || 'USER'] || user.userType || 'User'}
        </span>
      </td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[user.status] || statusStyles.INACTIVE}`}>
          {user.status}
        </span>
      </td>
      <td className="p-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-gray-400" />
          {new Date(user.createdAt).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </div>
      </td>
    </tr>
  )
})

UserRow.displayName = 'UserRow'

// ================================================================================
// FILTER BAR COMPONENT
// ================================================================================
const FilterBar = React.memo(({
  searchQuery,
  onSearchChange,
  filterRole,
  onRoleChange,
  filterUserType,
  onUserTypeChange,
  filterStatus,
  onStatusChange,
  onClear,
  hasActiveFilters,
}: {
  searchQuery: string
  onSearchChange: (value: string) => void
  filterRole: string
  onRoleChange: (value: string) => void
  filterUserType: string
  onUserTypeChange: (value: string) => void
  filterStatus: string
  onStatusChange: (value: string) => void
  onClear: () => void
  hasActiveFilters: boolean
}) => (
  <div className="space-y-3 mb-4">
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Advanced search: name, email, username, phone…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      {hasActiveFilters && (
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1 px-3 py-2.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
          Clear
        </button>
      )}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="relative">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <select
          value={filterRole}
          onChange={(e) => onRoleChange(e.target.value)}
          className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
        >
          <option value="">All Roles</option>
          <option value="USER">User</option>
          <option value="ADMIN">Admin</option>
        </select>
      </div>
      <div className="relative">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <select
          value={filterUserType}
          onChange={(e) => onUserTypeChange(e.target.value)}
          className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
        >
          <option value="">All User Types</option>
          <option value="USER">User</option>
          <option value="NGO">NGO</option>
          <option value="NIKSHAY_MITRA">Ni-kshay Mitra</option>
          <option value="SUPPORTER_INDIVIDUAL">Supporter (Individual)</option>
          <option value="SUPPORTER_NGO">Supporter (NGO)</option>
          <option value="SUPPORTER_CORPORATE">Supporter (Corporate)</option>
          <option value="NUTRITION_SPONSOR">Nutrition Sponsor</option>
          <option value="VOLUNTEER_COMMUNITY">Volunteer (Community)</option>
        </select>
      </div>
      <div className="relative">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <select
          value={filterStatus}
          onChange={(e) => onStatusChange(e.target.value)}
          className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="BANNED">Banned</option>
        </select>
      </div>
    </div>
  </div>
))

FilterBar.displayName = 'FilterBar'

// ================================================================================
// PAGINATION COMPONENT
// ================================================================================
const Pagination = React.memo(({
  page,
  totalPages,
  totalElements,
  onPrev,
  onNext,
}: {
  page: number
  totalPages: number
  totalElements: number
  onPrev: () => void
  onNext: () => void
}) => {
  if (totalPages <= 1) {
    return (
      <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-600">
          <span className="font-medium">{totalElements}</span> user{totalElements === 1 ? '' : 's'}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
      <GovButton
        variant="secondary"
        size="sm"
        onClick={onPrev}
        disabled={page === 0}
        className="flex items-center gap-1"
      >
        <ChevronLeft className="w-4 h-4" />
        Previous
      </GovButton>
      <span className="text-sm text-gray-600">
        Page <span className="font-medium">{page + 1}</span> of <span className="font-medium">{totalPages}</span> · {totalElements} total
      </span>
      <GovButton
        variant="secondary"
        size="sm"
        onClick={onNext}
        disabled={page >= totalPages - 1}
        className="flex items-center gap-1"
      >
        Next
        <ChevronRight className="w-4 h-4" />
      </GovButton>
    </div>
  )
})

Pagination.displayName = 'Pagination'

// ================================================================================
// LOADING SKELETON
// ================================================================================
const LoadingSkeleton = React.memo(() => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
        <div className="w-10 h-10 rounded-full bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/4" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
        </div>
        <div className="h-6 bg-gray-200 rounded w-20" />
      </div>
    ))}
  </div>
))

LoadingSkeleton.displayName = 'LoadingSkeleton'

// ================================================================================
// MAIN COMPONENT
// ================================================================================
const UserManagement: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterUserType, setFilterUserType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  // Debounced search value actually sent to the API
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasActiveFilters = !!(searchQuery || filterRole || filterUserType || filterStatus)

  // Auth check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') {
      navigate('/feed')
      return
    }
  }, [user, isAuthenticated, navigate])

  // Debounce the search input (300ms) so we don't fire a request per keystroke
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [searchQuery])

  // Fetch users whenever page or any filter (incl. debounced search) changes.
  // All filters are applied SERVER-SIDE via /admin/users query params.
  useEffect(() => {
    if (!user || (user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN')) return
    fetchUsers()
  }, [page, debouncedSearch, filterRole, filterUserType, filterStatus, user])

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('size', '10')
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim())
      if (filterRole) params.set('role', filterRole)
      if (filterUserType) params.set('userType', filterUserType)
      if (filterStatus) params.set('status', filterStatus)

      const response = await api.get(`/admin/users?${params.toString()}`)
      if (response.data.success) {
        setUsers(response.data.data.content)
        setTotalPages(response.data.data.totalPages)
        setTotalElements(response.data.data.totalElements)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, filterRole, filterUserType, filterStatus])

  // When any filter changes, reset to the first page
  const resetPage = useCallback(() => setPage(0), [])

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)
    resetPage()
  }, [resetPage])

  const handleRoleChange = useCallback((value: string) => {
    setFilterRole(value)
    resetPage()
  }, [resetPage])

  const handleUserTypeChange = useCallback((value: string) => {
    setFilterUserType(value)
    resetPage()
  }, [resetPage])

  const handleStatusChange = useCallback((value: string) => {
    setFilterStatus(value)
    resetPage()
  }, [resetPage])

  const handleClear = useCallback(() => {
    setSearchQuery('')
    setFilterRole('')
    setFilterUserType('')
    setFilterStatus('')
    resetPage()
  }, [resetPage])

  const handlePrevPage = useCallback(() => setPage(p => Math.max(0, p - 1)), [])
  const handleNextPage = useCallback(() => setPage(p => p + 1), [])

  const activeFilterCount = useMemo(() =>
    [filterRole, filterUserType, filterStatus, debouncedSearch].filter(Boolean).length,
  [filterRole, filterUserType, filterStatus, debouncedSearch])

  const exportColumns: ExportColumn<User>[] = [
    { header: 'ID', key: 'id' },
    { header: 'First Name', key: 'firstName' },
    { header: 'Last Name', key: 'lastName' },
    { header: 'Username', key: 'username' },
    { header: 'Email', key: 'email' },
    { header: 'Role', key: 'role' },
    { header: 'User Type', key: 'userType' },
    { header: 'Status', key: 'status' },
    { header: 'Email Verified', key: 'emailVerified', format: (v) => (v ? 'Yes' : 'No') },
    { header: 'Created', key: 'createdAt', format: (v) => new Date(v as string).toLocaleString() },
    { header: 'Last Login', key: 'lastLogin', format: (v) => (v ? new Date(v as string).toLocaleString() : '—') },
  ]

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle
          title="User Management"
          subtitle={`${totalElements.toLocaleString()} registered user${totalElements === 1 ? '' : 's'}${activeFilterCount ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied` : ''}`}
          icon={Users}
        />

        <GovCard>
          <div className="flex justify-end gap-2 mb-3">
            <GovButton variant="secondary" size="sm" onClick={() => exportToCSV('users', exportColumns, users)} disabled={!users.length}>
              <Download className="w-4 h-4 inline mr-1" /> CSV
            </GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => exportToPDF('users', 'Users Report', exportColumns, users)} disabled={!users.length}>
              <FileText className="w-4 h-4 inline mr-1" /> PDF
            </GovButton>
          </div>
          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            filterRole={filterRole}
            onRoleChange={handleRoleChange}
            filterUserType={filterUserType}
            onUserTypeChange={handleUserTypeChange}
            filterStatus={filterStatus}
            onStatusChange={handleStatusChange}
            onClear={handleClear}
            hasActiveFilters={hasActiveFilters}
          />

          {loading ? (
            <LoadingSkeleton />
          ) : users.length === 0 ? (
            <GovEmptyState
              icon="👥"
              title="No Users Found"
              description={hasActiveFilters ? "Try adjusting your search or filters" : "No users have registered yet"}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">User</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Email</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Role</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">User Type</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow key={u.id} user={u} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPrev={handlePrevPage}
            onNext={handleNextPage}
          />
        </GovCard>
      </div>
    </GovLayout>
  )
}

export default React.memo(UserManagement)